import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useState } from "react";
import { Text, View } from "react-native";
import { useTheme } from "@/theme/ThemeProvider";
import { AppButton } from "@/ui/components/AppButton";
import { FloatingLabelInput } from "@/ui/components/FloatingLabelInput";
import { SectionCard } from "@/ui/components/SectionCard";
import { getOfflineStore } from "./store";
import type { ConflictRecord, NoticeRecord } from "./store/types";
import {
  discardConflict,
  resubmitOccurrenceAsAdditional,
  retryConflictWithCurrentExpected,
} from "./sync/gateway";

export function OfflineConflictPanel() {
  const { theme } = useTheme();
  const qc = useQueryClient();
  const [reason, setReason] = useState("");
  const q = useQuery({
    queryKey: ["offline", "conflicts"],
    queryFn: async () => {
      const store = await getOfflineStore();
      return {
        conflicts: await store.getConflicts(),
        notices: await store.getNotices(),
      };
    },
    refetchInterval: 4000,
  });
  const conflicts: ConflictRecord[] = q.data?.conflicts ?? [];
  const notices: NoticeRecord[] = q.data?.notices ?? [];
  const visibleNotices = notices
    .filter((notice) => !notice.read)
    .sort((a, b) => (b.lastSeenAt ?? b.createdAt).localeCompare(a.lastSeenAt ?? a.createdAt))
    .slice(0, 5);
  if (conflicts.length === 0 && visibleNotices.length === 0) {
    return null;
  }

  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ["offline"] });
  };

  return (
    <SectionCard title={`Offline-Konflikte (${conflicts.length})`}>
      <View style={{ gap: theme.space.sm }}>
        {conflicts.map((conflict) => (
          <View
            key={conflict.id}
            style={{
              borderWidth: 1,
              borderColor: theme.color.border,
              borderRadius: theme.radius.md,
              padding: theme.space.sm,
              gap: theme.space.xs,
            }}
          >
            <Text style={{ color: theme.color.text, fontWeight: "700" }}>
              {conflict.kind === "occurrence"
                ? "Medikamentengabe bereits erledigt"
                : conflict.kind === "place"
                  ? "Unterbringungsplatz abweichend"
                  : conflict.kind === "diagnosis"
                    ? "Diagnosestand abweichend"
                    : "Synchronisierungskonflikt"}
            </Text>
            <Text style={{ color: theme.color.textSecondary, fontSize: 13 }}>
              {conflict.serverHint ??
                "Der lokale Eintrag bleibt vollständig erhalten."}
            </Text>
            {conflict.kind === "occurrence" ? (
              <FloatingLabelInput
                label="Begründung Zusatzgabe"
                value={reason}
                onChangeText={setReason}
              />
            ) : null}
            <View style={{ flexDirection: "row", gap: theme.space.xs, flexWrap: "wrap" }}>
              <AppButton
                variant="outline"
                onPress={async () => {
                  await discardConflict(conflict.id);
                  refresh();
                }}
              >
                Verwerfen
              </AppButton>
              {conflict.kind === "occurrence" ? (
                <AppButton
                  variant="primary"
                  onPress={async () => {
                    await resubmitOccurrenceAsAdditional(conflict.id, reason);
                    setReason("");
                    refresh();
                  }}
                >
                  Als Zusatzgabe
                </AppButton>
              ) : (
                <AppButton
                  variant="primary"
                  onPress={async () => {
                    await retryConflictWithCurrentExpected(conflict.id, {});
                    refresh();
                  }}
                >
                  Erneut einreichen
                </AppButton>
              )}
            </View>
          </View>
        ))}
        {visibleNotices.map((notice) => (
          <View key={notice.id} style={{ gap: theme.space.xs }}>
            <Text style={{ color: theme.color.textSecondary, fontSize: 13 }}>{notice.body}</Text>
            <AppButton
              variant="outline"
              accessibilityRole="button"
              accessibilityLabel={`Hinweis ausblenden: ${notice.body}`}
              onPress={async () => {
                try {
                  const store = await getOfflineStore();
                  await store.markNoticeRead(notice.id);
                  refresh();
                } catch (error) {
                  console.warn(
                    "OfflineConflictPanel: Hinweis ausblenden fehlgeschlagen",
                    error instanceof Error ? error.message : error,
                  );
                }
              }}
            >
              Ausblenden
            </AppButton>
          </View>
        ))}
      </View>
    </SectionCard>
  );
}

export function useOfflineNotices() {
  const [count, setCount] = useState(0);
  useEffect(() => {
    let alive = true;
    const tick = async () => {
      const store = await getOfflineStore();
      const [conflicts, notices, outbox] = await Promise.all([
        store.getConflicts(),
        store.getNotices(),
        store.getOutbox(),
      ]);
      if (!alive) return;
      setCount(
        conflicts.length +
          notices.filter((n) => !n.read).length +
          outbox.filter((o) => o.status === "quarantine" || o.status === "pending").length
      );
    };
    const run = () => {
      void tick().catch((err: unknown) => {
        console.error(
          "OfflineConflictPanel: store read failed",
          err instanceof Error ? err.message : err,
        );
      });
    };
    run();
    const id = setInterval(run, 4000);
    return () => {
      alive = false;
      clearInterval(id);
    };
  }, []);
  const refresh = useCallback(async () => {
    const store = await getOfflineStore();
    return store.getConflicts();
  }, []);
  return { count, refresh };
}
